import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Textarea } from '@amore/ui/components/textarea'
import { ArrowRight, Check, UserRoundPlus } from 'lucide-react'
import { useState } from 'react'
import { newId, plural } from '@/lib/format'
import { circleLabels } from '@/lib/labels'
import { useUpdate, useWedding } from '@/mock/store'
import type { GuestCircle, Party } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { parseGuestList } from './bulk'
import { partyTitle, personName } from './model'

const example = `Martin a Lucie Dvořákovi
Ondřej Svoboda +1
Jana Nováková, Karel Novák
Kolegyně z práce: Simona Benešová a Hana Králová`

const circles = (['bride', 'groom', 'shared'] as GuestCircle[]).map((value) => ({
  value,
  label: circleLabels[value],
}))

const cheatSheet = [
  { write: 'Jana Nováková', result: '1 host' },
  { write: 'Jana Nováková, Karel Novák', result: 'pár, 2 hosté' },
  { write: 'Petra a Tomáš Horákovi', result: 'Petra Horáková, Tomáš Horák' },
  { write: 'Michal Kraus +1', result: 'Michal + doprovod, jméno doplní host' },
  { write: 'Kolegyně: Eva Malá, Hana Veselá', result: 'pozvánka „Kolegyně“ pro 2 hosty' },
]

/**
 * "Vložit seznam": paste names (from notes, a spreadsheet, a message), one
 * invitation per line, and see how they'll be split before adding them all.
 * Answers, diets etc. come later from the guests' replies.
 */
export function BulkDialog({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const [text, setText] = useState('')
  const [circle, setCircle] = useState<GuestCircle>('shared')
  const [keep, setKeep] = useState<Set<string>>(new Set())
  const [error, setError] = useState(false)
  const parsed = parseGuestList(text, circle, data.parties)
  const chosen = parsed.filter((item) => !item.duplicate || keep.has(item.key))
  const people = chosen.reduce((sum, item) => sum + item.persons.length, 0)
  const allParts = data.dayParts.map((part) => part.id)

  const add = () => {
    if (chosen.length === 0) {
      setError(true)
      return
    }
    const added: Party[] = chosen.map((item) => ({
      id: newId('party'),
      circle: item.circle,
      persons: item.persons,
      ...(item.name ? { name: item.name } : {}),
      parts: allParts,
      replied: false,
      accommodation: false,
      transport: false,
    }))
    const ids = new Set(added.map((party) => party.id))
    update((current) => ({ ...current, parties: [...current.parties, ...added] }))
    showToast({
      title: `${invitations(added.length, true)} ${plural(added.length, 'přidána', 'přidány', 'přidáno')}`,
      description: `${people} ${plural(people, 'host', 'hosté', 'hostů')} čeká na odpověď`,
      tone: 'success',
      action: {
        label: 'Vrátit zpět',
        altText: 'Odebrat právě přidané pozvánky',
        onClick: () =>
          update((current) => ({
            ...current,
            parties: current.parties.filter((party) => !ids.has(party.id)),
          })),
      },
    })
    onClose()
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      width="wide"
      eyebrow="Hosté"
      title="Vložit seznam hostů"
      description="Jedna pozvánka na řádek. Jména zkopírujte z poznámek nebo tabulky."
      dirty={text.trim().length > 0}
      primaryAction={{
        label: chosen.length ? `Přidat ${invitations(chosen.length)}` : 'Přidat pozvánky',
        onClick: add,
      }}
    >
      {/* On desktop both columns fill the sheet; the preview scrolls on its own. */}
      <div className="grid gap-6 md:h-full md:grid-cols-2">
        <div className="flex min-h-0 flex-col gap-4">
          <div className="self-start">
            <Segmented
              label="Čí jsou to hosté"
              options={circles}
              value={circle}
              onChange={setCircle}
            />
          </div>
          <Textarea
            value={text}
            onChange={(event) => {
              setText(event.target.value)
              setError(false)
            }}
            placeholder={'Jana Nováková\nPetra a Tomáš Horákovi\nMichal Kraus +1'}
            aria-label="Seznam jmen"
            data-autofocus
            className="min-h-60 flex-1 resize-none border-transparent bg-muted/60 font-[inherit] leading-7 hover:bg-muted focus:bg-card"
          />
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold">Náhled</span>
            {chosen.length ? (
              <span className="text-xs text-muted-foreground">
                {invitations(chosen.length)} · {people} {plural(people, 'host', 'hosté', 'hostů')}
              </span>
            ) : null}
          </div>
          {parsed.length === 0 ? (
            <div
              className={cn(
                'min-h-0 space-y-4 overflow-y-auto rounded-panel border border-dashed p-5',
                error ? 'border-error-foreground/60' : 'border-border',
              )}
            >
              <div className="space-y-1">
                <p className="text-sm font-semibold">Jak psát seznam</p>
                <p
                  className={cn(
                    'text-xs',
                    error ? 'text-error-foreground' : 'text-muted-foreground',
                  )}
                >
                  {error
                    ? 'Vložte alespoň jedno jméno.'
                    : 'Jedna pozvánka na řádek. Tady pak uvidíte, jak se seznam rozdělí.'}
                </p>
              </div>
              <dl className="space-y-3 text-sm">
                {cheatSheet.map((row) => (
                  <div key={row.write} className="space-y-0.5">
                    <dt className="font-medium">{row.write}</dt>
                    <dd className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <ArrowRight aria-hidden="true" className="size-3 shrink-0" />
                      {row.result}
                    </dd>
                  </div>
                ))}
              </dl>
              <Button
                size="sm"
                appearance="outline"
                tone="neutral"
                onClick={() => setText(example)}
              >
                Vyzkoušet na příkladu
              </Button>
            </div>
          ) : (
            <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto overscroll-contain pr-2">
              {parsed.map((item) => {
                const skipped = item.duplicate && !keep.has(item.key)
                return (
                  <li
                    key={item.key}
                    className={cn('space-y-1.5 py-3 first:pt-0', skipped && 'opacity-55')}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className={cn('text-sm font-medium', skipped && 'line-through')}>
                        {partyTitle({
                          id: item.key,
                          ...emptyParty,
                          name: item.name,
                          persons: item.persons,
                        })}
                      </span>
                      <Badge className="shrink-0">{circleLabels[item.circle]}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {item.persons.map((person) => (
                        <span
                          key={person.id}
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs"
                        >
                          {person.plusOne ? (
                            <UserRoundPlus aria-hidden="true" className="size-3" />
                          ) : null}
                          {person.plusOne ? 'doprovod' : personName(person)}
                        </span>
                      ))}
                    </div>
                    {item.duplicate ? (
                      <button
                        type="button"
                        onClick={() =>
                          setKeep((current) => {
                            const next = new Set(current)
                            if (next.has(item.key)) next.delete(item.key)
                            else next.add(item.key)
                            return next
                          })
                        }
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-warning-foreground hover:underline"
                      >
                        {skipped ? (
                          'Už je v seznamu, přeskočíme · Přesto přidat'
                        ) : (
                          <>
                            <Check aria-hidden="true" className="size-3.5" />
                            Přidáme znovu · Přeskočit
                          </>
                        )}
                      </button>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
          {chosen.length ? (
            <p className="text-xs text-muted-foreground">
              Všichni dostanou pozvánku na celý den a budou čekat na odpověď. Upravit to můžete u
              každé pozvánky.
            </p>
          ) : null}
        </div>
      </div>
    </DetailSheet>
  )
}

const emptyParty = {
  circle: 'shared' as const,
  parts: [],
  replied: false,
  accommodation: false,
  transport: false,
}

const invitations = (count: number, capital = false) =>
  `${count} ${plural(count, capital ? 'pozvánka' : 'pozvánku', 'pozvánky', 'pozvánek')}`
